"use client"

import { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { createTheme } from "@mui/material/styles" // Import createTheme to declare theme
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  InputBase,
  Badge,
  Avatar, // Material UI Avatar
  Menu,
  MenuItem,
  Box,
  Divider,
  Button,
} from "@mui/material"
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Lock,
  Home,
  ExitToApp,
} from "@mui/icons-material"
import { styled, alpha } from "@mui/material/styles"
import { useLocation, useParams } from "react-router-dom";
import { useContext } from "react";
import ProfileContext from "../../contexts/ProfileContext";
import { Link } from "react-router-dom";

const theme = createTheme() // Declare theme variable

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.05),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.black, 0.08),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}))

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}))

const Header = ({ sidebarOpen, setSidebarOpen, onLogout, isMobile }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [notificationCount] = useState(3)
  const navigate = useNavigate()
  const location = useLocation();
  const { profile } = useContext(ProfileContext);
  const { id } = useParams();

  const pathToTitle = {
    "/": "Dashboard",
    "/admin/profile": "Admin Profile",
    "/admin/change-password": "Change Password",
    "/admin/all_admin": "All Admin",
    "/admin/add_admin": "Add Admin",
    "/admin/multi_admin": "Add Multi Admin",

    "/admin/all_permission": "All Permission",
    "/admin/add_permission": "Add Permission",

    "/admin/all_role": "All Roles",
    "/admin/add_role": "Add Roles",


    "/admin/add_role_permission": "Add RolePermission",
    "/admin/all_role_permission": "All RolePermission",



    "/settings": "Settings",
    "/sales/sale-manage": "Sale Manage",
    "/sales/order-manage": "Order Manage",
    "/sales/all-sales-reports": "All Sales Report",
    "/accounts/payment-receive": "Payment Receive",
    "/accounts/team_wise-payment": "Team_wise_Payment Receive",
    // আরো পেজ এড করতে পারো এখানে



  };

  // const pageTitle = pathToTitle[location.pathname] || "Dashboard";

  // নিচের অংশ যদি না রাখতে চাও তাহলে নিচেরটা ডিলিট করে দিয়ে উপরের লাইনটা uncomment করে দিতে হবে  

  let pageTitle = "Dashboard";

  if (location.pathname.startsWith("/admin/edit-admin/")) {
    pageTitle = "Edit Admin";
  } else if (location.pathname.startsWith("/admin/edit-permission/")) {
    pageTitle = "Edit Permission";
  } else if (location.pathname.startsWith("/admin/edit-role/")) {
    pageTitle = "Edit Role";
  } else if (location.pathname.startsWith("/admin/edit-role-permission/")) {
    pageTitle = "Edit Role Permission";
  } else {
    pageTitle = pathToTitle[location.pathname] || "Dashboard";
  }


  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const memoizedOnLogout = useCallback(() => {
    if (typeof onLogout === "function") {
      onLogout()
    } else {
      console.error("onLogout is not a function in Header.js")
    }
  }, [onLogout])

  const profileMenuItems = [
    // নিচেরটা নেভিগেট ব্যবহার করে
    // { icon: AccountCircle, label: "Profile", action: () => navigate("admin/profile") },
    // { icon: Lock, label: "Change Password", action: () => navigate("admin/change-password") },
    // { icon: Home, label: "Dashboard", action: () => navigate("/dashboard") },

    // নিচেরটা লিংক ব্যবহার করে, mouse right button open link in new tab পাওয়ার জন্য
    { icon: AccountCircle, label: "Profile", href: "/admin/profile" },
    { icon: Lock, label: "Change Password", href: "/admin/change-password" },
    { icon: Home, label: "Dashboard", href: "/admin/dashboard" },
    {
      icon: ExitToApp,
      label: "Logout",
      action: memoizedOnLogout,
      danger: true,
    },
  ]

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        {/* Dashboard Title & Welcome text - Hide on xs and sm screens */}
        <Box sx={{ flexGrow: 0, mr: 3, display: { xs: "none", md: "block" } }}>
          <Typography variant="h6" noWrap component="div" sx={{ color: "#333" }}>
            {/* Dashboard */}
            {pageTitle}
          </Typography>
          <Typography variant="caption" sx={{ color: "#666" }}>
            Welcome to ERP Management System
          </Typography>
        </Box>
        {/* Search Bar - Hide on xs and sm screens */}
        <Search sx={{ display: { xs: "none", md: "block" } }}>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase placeholder="Search..." inputProps={{ "aria-label": "search" }} />
        </Search>
        <Box sx={{ flexGrow: 1 }} /> {/* Spacer */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Settings Icon - Always visible */}
          <IconButton color="inherit">
            <SettingsIcon />
          </IconButton>

          {/* Notifications Icon - Always visible */}
          <IconButton color="inherit">
            <Badge badgeContent={notificationCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Button
            onClick={handleProfileMenuOpen}
            sx={{
              textTransform: "none",
              color: "inherit",
              p: 1,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: alpha(theme.palette.common.black, 0.05),
              },
              ml: 1,
            }}
          >
            <Avatar
              sx={{ width: 40, height: 40, bgcolor: "primary.main", mr: 1.5 }}
              src={profile.photo}
              alt={profile.name}
            >
              {profile.name?.[0]}
            </Avatar>
            {/* Name/Email - Hide on xs screens, show on sm and up */}
            <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "left" }}>
              <Typography variant="subtitle2" sx={{ lineHeight: 1.2, fontWeight: "medium" }}>
                {profile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {profile.email}
              </Typography>
            </Box>
          </Button>
        </Box>
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >

          <Divider />

          {/* // নিচেরটা নেভিগেট ব্যবহার করে */}
          {/* {profileMenuItems.map((item, index) => {
            const Icon = item.icon
            return (
              <MenuItem
                key={index}
                 component={item.href ? "a" : "div"}
                onClick={() => {
                  item.action()
                  handleMenuClose()
                }}
                sx={{ color: item.danger ? "error.main" : "inherit" }}
              >
                <Icon sx={{ mr: 2, fontSize: 20 }} />
                {item.label}
              </MenuItem>
            )
          })} */}

          {/*  নিচেরটা লিংক ব্যবহার করে, mouse right button open link in new tab পাওয়ার জন্য */}

          {profileMenuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <MenuItem
                key={index}
                component={item.href ? Link : "div"}
                to={item.href || undefined}
                onClick={
                  item.action
                    ? () => {
                      item.action();
                      handleMenuClose();
                    }
                    : handleMenuClose
                }
                sx={{ color: item.danger ? "error.main" : "inherit" }}
                target={item.newTab ? "_blank" : undefined} // চাইলে নতুন ট্যাব ডিফল্ট করতে
              >
                <Icon sx={{ mr: 2, fontSize: 20 }} />
                {item.label}
              </MenuItem>
            );
          })}

        </Menu>
      </Toolbar>
    </AppBar>
  )
}

export default Header
